/**
 * Number / currency formatting utilities.
 *
 * Faithful port of `RW-ERP-Service/src/utils/number-utils.ts`. Functions
 * preserve their original signatures and edge-case behaviour so output
 * matches the Vue ERP byte-for-byte:
 *
 *   - `formatNumber(value)`            — Intl decimal with 0–2 fraction digits
 *   - `formatNumberToDecimal(value, n)` — same but configurable decimals
 *   - `currencyConverter(...)`          — formatted + abbreviated pair
 *   - `formateNumber(...)`              — plain-number version (Vue typo kept)
 *   - `abbreviateNumber(...)`           — adds K/Mn/Bn/T/P with currency
 *   - `abbreviateNumberValue(...)`      — adds K/Mn/Bn/T/P without symbol
 *
 * Returned shape `{ formattedValues, abstractValues }` matches Vue:
 *   - `formattedValues` is what's rendered inline (abbreviated when abstract=true)
 *   - `abstractValues` is the full unabbreviated value, used for tooltips
 */
import {
  CURRENCY,
  CURRENCY_CODES,
  CURRENCY_SUFFIX,
  DEFAULT_CURRENCY_CODE,
  DEFAULT_LOCALE,
} from '../constants/currency';
import { logger } from './logger';

const BLANK_DATA = '— —';

export interface FormattedNumber {
  formattedValues: string;
  abstractValues: string;
}

function isNotANumber(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  const n = Number(value);
  return Number.isNaN(n);
}

/**
 * Validates a currency code; falls back to USD on unknown input.
 * Mirrors `getValidCurrencyCode()` (kept private in the Vue file).
 */
export function getValidCurrencyCode(code: string | null | undefined): string {
  if (!code) return DEFAULT_CURRENCY_CODE;
  const upper = code.toUpperCase();
  return CURRENCY.includes(upper) ? upper : DEFAULT_CURRENCY_CODE;
}

/**
 * Formats a number with locale-aware comma separators. By default uses 0
 * minimum and 2 maximum fraction digits — matches `formatNumber` in the
 * Vue util when called without an `attributeCode`.
 */
export function formatNumber(value: number): string {
  if (Number.isNaN(value)) return String(value);
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Locale-aware comma format with explicit decimal limit. Mirrors
 * `formatNumberToDecimal()`.
 */
export function formatNumberToDecimal(
  value: number,
  decimalLimit: number = 2
): string {
  if (Number.isNaN(value)) return String(value);
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: decimalLimit,
    maximumFractionDigits: decimalLimit,
  }).format(value);
}

/**
 * Abbreviates a numeric value with a currency symbol prefix:
 *   1500    → "$1.50K"
 *   1234567 → "$1.23Mn"
 *   42      → "$42"   (no suffix when below 1k)
 *
 * Mirrors `abbreviateNumber()` in the Vue util.
 */
export function abbreviateNumber(
  value: number,
  currencySymbol: string,
  decimalLimit: number
): string {
  const abs = Math.abs(value);
  // Match Vue's logic: `Math.floor(Math.log10(abs) / 3)` — 0 for <1000,
  // 1 for thousands, 2 for millions, etc.
  const tier = abs > 0 ? Math.max(0, Math.floor(Math.log10(abs) / 3)) : 0;

  let shortValue: string;
  if (value > 1000) {
    shortValue = (abs / Math.pow(1000, tier)).toFixed(decimalLimit);
  } else if (decimalLimit === 0) {
    shortValue = String(abs / Math.pow(1000, tier));
  } else {
    shortValue = (abs / Math.pow(1000, tier)).toFixed(decimalLimit);
  }

  const sign = value < 0 ? '-' : '';

  if (tier === 0) {
    return `${sign}${currencySymbol}${shortValue}`;
  }
  return `${sign}${currencySymbol}${shortValue}${CURRENCY_SUFFIX[tier - 1]}`;
}

/**
 * Abbreviates a numeric value without a currency symbol:
 *   1500    → "1.50K"
 *   1234567 → "1.23Mn"
 *
 * Mirrors `abbreviateNumberValue()` in the Vue util.
 */
export function abbreviateNumberValue(
  value: number,
  decimalLimit: number
): string {
  const abs = Math.abs(value);
  const tier = abs > 0 ? Math.max(0, Math.floor(Math.log10(abs) / 3)) : 0;

  let shortValue = (abs / Math.pow(1000, tier)).toFixed(decimalLimit);
  if (value < 0) shortValue = `-${shortValue}`;

  if (tier === 0) return shortValue;
  return `${shortValue}${CURRENCY_SUFFIX[tier - 1]}`;
}

/**
 * Converts an amount to a formatted currency pair: the inline (optionally
 * abbreviated) value and the full Intl-formatted value used for tooltips.
 *
 * Note: when `abstract: true` and the amount is a thousand or more, the
 * abbreviated value is built manually (`abbreviateNumber`). When the
 * amount is below 1000 OR `abstract: false`, we fall back to
 * `Intl.NumberFormat(...)` so locale-specific symbols/grouping still apply.
 */
export function currencyConverter(
  amount: string | number,
  decimalLimit: number,
  currencyCode: string,
  locale: string = DEFAULT_LOCALE,
  showSymbol: boolean = true,
  abstract: boolean = true
): FormattedNumber {
  if (isNotANumber(amount) || isNotANumber(decimalLimit)) {
    return { formattedValues: BLANK_DATA, abstractValues: BLANK_DATA };
  }

  const validCurrencyCode = getValidCurrencyCode(currencyCode);
  const numeric = Number(amount);

  const options: Intl.NumberFormatOptions = {
    style: showSymbol ? 'currency' : 'decimal',
    currency: validCurrencyCode,
    minimumFractionDigits: decimalLimit,
    maximumFractionDigits: decimalLimit,
  };

  const formatter = new Intl.NumberFormat(locale, options);
  const fullFormatted = formatter.format(numeric);

  let abstractValue = fullFormatted;
  if (abstract) {
    // Extract the currency symbol from the formatted output (handles "$",
    // "€", "₹", "CA$", ...). When `showSymbol` is false, prefix with sign only.
    const negativeSign = numeric < 0 ? '-' : '';
    const currencySymbol = showSymbol
      ? (fullFormatted.match(/[^\d.,\s-]/g) ?? []).join('')
      : negativeSign;
    abstractValue = abbreviateNumber(numeric, currencySymbol, decimalLimit);
  }

  return { formattedValues: abstractValue, abstractValues: fullFormatted };
}

/**
 * Plain-number twin of `currencyConverter`. The argument and method names
 * preserve the Vue-side typo (`formateNumber`) so cross-app grep and any
 * shared docs continue to match.
 */
export function formateNumber(
  value: string | number,
  decimalLimit: number | null,
  locale: string = DEFAULT_LOCALE,
  abstract: boolean = true
): FormattedNumber {
  if (isNotANumber(value)) {
    return { formattedValues: BLANK_DATA, abstractValues: BLANK_DATA };
  }

  const numeric = Number(value);
  let resolvedDecimal = decimalLimit;
  resolvedDecimal ??= numeric % 1 === 0 ? 0 : 2;

  const options: Intl.NumberFormatOptions = {
    minimumFractionDigits: resolvedDecimal,
    maximumFractionDigits: resolvedDecimal,
  };

  const formatter = new Intl.NumberFormat(locale, options);
  const fullFormatted = formatter.format(numeric);

  let abstractValue = fullFormatted;
  if (abstract) {
    abstractValue = abbreviateNumberValue(numeric, resolvedDecimal);
  }

  return { formattedValues: abstractValue, abstractValues: fullFormatted };
}

/**
 * `amount` as a currency string, or `na` when there is no amount to show
 * (absent or NaN). An unknown currency code renders in the default
 * currency rather than throwing. Shared by the Orders and Staged Orders
 * mappers, each of which binds its own N/A sentinel.
 */
export function formatMoney(
  amount: number | undefined,
  currency: string | undefined,
  na: string,
): string {
  if (amount === undefined || amount === null) return na;
  if (Number.isNaN(amount)) return na;
  try {
    return new Intl.NumberFormat(DEFAULT_LOCALE, {
      style: 'currency',
      currency: currency ?? DEFAULT_CURRENCY_CODE,
    }).format(amount);
  } catch {
    // Not swallowed: a malformed code is an upstream data problem worth a trace.
    logger.warn('[numberUtils] invalid currency code, falling back to default', {
      currency,
    });
    return new Intl.NumberFormat(DEFAULT_LOCALE, {
      style: 'currency',
      currency: DEFAULT_CURRENCY_CODE,
    }).format(amount);
  }
}

// Convenience re-exports so callers don't have to import from two places.
export { CURRENCY, CURRENCY_CODES, CURRENCY_SUFFIX, DEFAULT_CURRENCY_CODE, DEFAULT_LOCALE };
